import { ALL_HPO_TERMS, type HPOSuggestion, type HPOTerm } from '@/components/common/hpoTypes'
import { useHpoRemoteSearch } from '@/components/common/useHpoRemoteSearch'
import { getRelatedTerms } from '@/utils/hpoRelationships'
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Close, Search } from 'flowbite-react-icons/outline'
import { Button } from '@helix-ds/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface ExtendedSuggestion extends HPOSuggestion {
  relationship?: 'parent' | 'child' | 'sibling'
}

interface HPOSearchModalBaseProps {
  isOpen: boolean
  onClose: () => void
  patientPhenotypes?: HPOSuggestion[]
  showSelectedTerms?: boolean
  minQueryLength?: number
}

interface HPOSearchModalAddProps extends HPOSearchModalBaseProps {
  mode?: 'add'
  onSelect: (term: HPOSuggestion) => void
  onRemove: (termId: string) => void
  selectedTermIds: string[]
  selectedTerms: HPOTerm[]
  swapTerm?: never
  onSwap?: never
}

interface HPOSearchModalSwapProps extends HPOSearchModalBaseProps {
  mode: 'swap'
  swapTerm: HPOTerm
  onSwap: (newTerm: HPOTerm) => void
  existingTermIds: string[]
  onSelect?: never
  onRemove?: never
  selectedTermIds?: never
  selectedTerms?: never
}

type HPOSearchModalProps = HPOSearchModalAddProps | HPOSearchModalSwapProps

const ANIMATION_DURATION = 0.24

const searchModalVariants = {
  enter: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: ANIMATION_DURATION, ease: [0.32, 0.72, 0, 1] },
  },
  exit: {
    opacity: 0,
    y: -60,
    scale: 0.98,
    transition: { duration: ANIMATION_DURATION, ease: [0.32, 0.72, 0, 1] },
  },
  initial: {
    opacity: 0,
    y: 60,
    scale: 0.98,
  },
}

const searchBackdropVariants = {
  enter: { opacity: 1, transition: { duration: ANIMATION_DURATION } },
  exit: { opacity: 0, transition: { duration: ANIMATION_DURATION } },
  initial: { opacity: 0 },
}

const MAX_RESULTS = 15

function HighlightedText({ text, searchQuery }: { text: string; searchQuery: string }) {
  if (!searchQuery) {
    return <span>{text}</span>
  }

  const lowerText = text.toLowerCase()
  const lowerQuery = searchQuery.toLowerCase()
  const index = lowerText.indexOf(lowerQuery)

  if (index === -1) {
    return <span>{text}</span>
  }

  const before = text.slice(0, index)
  const match = text.slice(index, index + searchQuery.length)
  const after = text.slice(index + searchQuery.length)

  return (
    <span>
      <span className="text-text-body dark:text-gray-400">{before}</span>
      <span className="font-semibold text-brand-700 dark:text-brand-400">{match}</span>
      <span className="text-text-body dark:text-gray-400">{after}</span>
    </span>
  )
}

function TypeBadge({ type }: { type: 'key' | 'additional' }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium dark:bg-gray-800 ${type === 'key' ? 'bg-brand-100 text-brand-700 dark:text-brand-400' : 'bg-background-tertiary text-text-body dark:text-gray-400'}`}
    >
      {type === 'key' ? 'Key' : 'Additional'}
    </span>
  )
}

function RelationshipBadge({ relationship }: { relationship: 'parent' | 'child' | 'sibling' }) {
  const config = {
    parent: { label: 'Parent', icon: ArrowUp, className: 'bg-brand-50 text-brand-700 border-brand-200 dark:bg-gray-800 dark:text-brand-400 dark:border-brand-800' },
    child: { label: 'Child', icon: ArrowDown, className: 'bg-success-50 text-text-fg-success border-border-success-subtle dark:bg-gray-900 dark:text-success-100 dark:border-success-700' },
    sibling: { label: 'Sibling', icon: ArrowRight, className: 'bg-info-50 text-info-600 border-info-100 dark:bg-gray-900 dark:text-info-100 dark:border-info-600' },
  }

  const { label, icon: Icon, className } = config[relationship]

  return (
    <span className={`inline-flex items-center gap-1 rounded-base border px-2 py-0.5 text-xs font-medium ${className}`}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  )
}

function truncateText(text: string, maxLength: number = 20): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength - 1) + '...'
}

export function HPOSearchModal(props: HPOSearchModalProps) {
  const { isOpen, onClose, showSelectedTerms = true } = props
  const isSwapMode = props.mode === 'swap'
  const minQueryLength = isSwapMode ? 0 : (props.minQueryLength ?? 3)
  const swapTermId = props.swapTerm?.id ?? null

  const [searchQuery, setSearchQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  /** Picked from the current results; they reappear only with the next result set. */
  const [pickedIds, setPickedIds] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const { results: remoteSuggestions, isSearching } = useHpoRemoteSearch(searchQuery, {
    enabled: isOpen && !isSwapMode,
    minQueryLength,
    limit: MAX_RESULTS,
  })

  const excludedIds = isSwapMode ? props.existingTermIds : props.selectedTermIds

  const relatedTerms = useMemo(() => {
    if (!isSwapMode || !swapTermId) return []
    return getRelatedTerms(swapTermId).filter((t) => !excludedIds.includes(t.id))
  }, [excludedIds, isSwapMode, swapTermId])

  const relatedTermsMap = useMemo(() => {
    const map = new Map<string, 'parent' | 'child' | 'sibling'>()
    for (const term of relatedTerms) {
      map.set(term.id, term.relationship)
    }
    return map
  }, [relatedTerms])

  const suggestions = useMemo((): ExtendedSuggestion[] => {
    const query = searchQuery.trim().toLowerCase()
    const isQueryReady = query.length >= minQueryLength

    if (isSwapMode) {
      if (!query) {
        return relatedTerms
      }

      const allMatches = ALL_HPO_TERMS.filter(
        (term) =>
          (term.name.toLowerCase().includes(query) || term.hpoCode.toLowerCase().includes(query)) &&
          !excludedIds.includes(term.id) &&
          term.id !== swapTermId
      )

      const familyMatches: ExtendedSuggestion[] = []
      const otherMatches: ExtendedSuggestion[] = []

      for (const term of allMatches) {
        const relationship = relatedTermsMap.get(term.id)
        if (relationship) {
          familyMatches.push({ ...term, relationship })
        } else {
          otherMatches.push(term)
        }
      }

      return [...familyMatches, ...otherMatches].slice(0, MAX_RESULTS)
    }

    if (!isQueryReady) {
      return []
    }

    return remoteSuggestions
      .filter((term) => !excludedIds.includes(term.id) && !pickedIds.includes(term.id))
      .slice(0, MAX_RESULTS)
  }, [
    searchQuery,
    excludedIds,
    pickedIds,
    isSwapMode,
    relatedTerms,
    relatedTermsMap,
    swapTermId,
    minQueryLength,
    remoteSuggestions,
  ])

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('')
      setHighlightedIndex(0)
      setPickedIds([])
      if (!isSwapMode) {
        requestAnimationFrame(() => {
          inputRef.current?.focus()
        })
      }
    }
  }, [isOpen, isSwapMode])

  useEffect(() => {
    setPickedIds([])
  }, [remoteSuggestions])

  useEffect(() => {
    setHighlightedIndex(0)
  }, [suggestions])

  useEffect(() => {
    if (listRef.current && highlightedIndex >= 0) {
      const highlightedEl = listRef.current.querySelector(`[data-index="${highlightedIndex}"]`)
      highlightedEl?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex])

  const handleSelect = (suggestion: ExtendedSuggestion) => {
    if (isSwapMode) {
      const newTerm: HPOTerm = {
        id: suggestion.id,
        name: suggestion.name,
        hpoCode: suggestion.hpoCode,
        type: props.swapTerm.type,
      }
      props.onSwap(newTerm)
      onClose()
    } else {
      props.onSelect(suggestion)
      setPickedIds((prev) => [...prev, suggestion.id])
      setHighlightedIndex(0)
      inputRef.current?.focus()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        e.stopPropagation()
        setHighlightedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        e.stopPropagation()
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1))
        break
      case 'Enter':
        e.preventDefault()
        e.stopPropagation()
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          handleSelect(suggestions[highlightedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        e.stopPropagation()
        onClose()
        break
    }
  }

  useEffect(() => {
    if (!isOpen) return

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown, true)
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true)
    }
  }, [isOpen, onClose])

  const getSectionHeader = () => {
    const query = searchQuery.trim()
    if (isSwapMode) {
      return query ? `Results for "${query}"` : 'Related Terms'
    }
    return query.length >= minQueryLength ? `Results for "${query}"` : ''
  }

  return createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          variants={searchBackdropVariants}
          initial="initial"
          animate="enter"
          exit="exit"
          className="bg-background-dark/60 fixed inset-0 z-modal flex items-center justify-center p-4"
          onClick={(e) => {
            e.stopPropagation()
            if (e.target === e.currentTarget) onClose()
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <motion.div
            variants={searchModalVariants}
            initial="initial"
            animate="enter"
            exit="exit"
            className="flex max-h-[min(88vh,calc(100vh-2rem))] w-full max-w-2xl flex-col overflow-hidden rounded-base bg-white shadow-2xl dark:bg-gray-900"
          >
            <div className="flex shrink-0 items-center gap-3 border-b border-border-light p-4 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="flex size-10 items-center justify-center rounded-base text-text-subtle transition-colors hover:bg-background-tertiary hover:text-text-body dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-400"
                aria-label="Back"
              >
                <ArrowLeft className="size-5" />
              </button>
              <div className="flex flex-1 flex-col gap-1">
                {isSwapMode && (
                  <p className="text-xs text-text-body dark:text-gray-400">
                    Replacing "<span className="font-medium text-text-body dark:text-gray-400">{props.swapTerm.name}</span>"
                  </p>
                )}
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-text-subtle dark:text-gray-500" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={isSwapMode ? 'Search for a different term...' : 'Search HPO terms by name or code...'}
                    className="w-full rounded-base border-0 bg-background-secondary-medium py-3 pl-12 pr-4 text-base text-text-heading placeholder:text-text-subtle outline-none ring-2 ring-transparent transition-shadow focus:bg-white focus:ring-brand-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500 dark:focus:bg-gray-900"
                    autoComplete="off"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery('')
                        inputRef.current?.focus()
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-base p-1 text-text-subtle transition-colors hover:bg-background-tertiary hover:text-text-body dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-400"
                      aria-label="Clear search"
                    >
                      <Close className="size-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto">
              {getSectionHeader() && (
                <div className="bg-background-secondary-medium/80 sticky top-0 border-b border-border-light px-4 py-2 backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800">
                  <span className="text-xs font-semibold uppercase tracking-wide text-text-body dark:text-gray-400">
                    {getSectionHeader()}
                  </span>
                </div>
              )}

              {suggestions.length > 0 ? (
                <div className="p-2">
                  {suggestions.map((suggestion, idx) => (
                    <button
                      key={suggestion.id}
                      data-index={idx}
                      type="button"
                      onClick={() => handleSelect(suggestion)}
                      onMouseEnter={() => setHighlightedIndex(idx)}
                      className={`flex w-full items-center justify-between gap-4 rounded-base px-4 py-3 text-left transition-all ${
                        highlightedIndex === idx ? 'bg-brand-50 shadow-sm dark:bg-gray-800' : 'hover:bg-background-secondary-medium dark:hover:bg-gray-800'
                      }`}
                    >
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="text-sm font-medium text-text-heading dark:text-white">
                          <HighlightedText text={suggestion.name} searchQuery={searchQuery} />
                        </span>
                        <span className="text-xs text-text-body dark:text-gray-400">{suggestion.hpoCode}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {suggestion.relationship && <RelationshipBadge relationship={suggestion.relationship} />}
                        {!isSwapMode && suggestion.type && <TypeBadge type={suggestion.type} />}
                        {highlightedIndex === idx && (
                          <span className="text-xs text-text-subtle dark:text-gray-500">{isSwapMode ? '↵ to swap' : '↵ to add'}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Search className="mb-3 size-10 text-text-disabled dark:text-gray-600" />
                  {searchQuery ? (
                    <>
                      {searchQuery.trim().length < minQueryLength ? (
                        <p className="text-sm font-medium text-text-body dark:text-gray-400">Start typing to search</p>
                      ) : isSearching ? (
                        <p className="text-sm font-medium text-text-body dark:text-gray-400">Searching HPO terms...</p>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-text-body dark:text-gray-400">No HPO terms found for "{searchQuery}"</p>
                          <p className="mt-1 text-xs text-text-subtle dark:text-gray-500">Try a different search term</p>
                        </>
                      )}
                    </>
                  ) : isSwapMode ? (
                    <>
                      <p className="text-sm font-medium text-text-body dark:text-gray-400">No related terms available</p>
                      <p className="mt-1 text-xs text-text-subtle dark:text-gray-500">Try searching for a different term</p>
                    </>
                  ) : (
                    <p className="text-sm font-medium text-text-body dark:text-gray-400">Start typing to search</p>
                  )}
                </div>
              )}
            </div>

            {!isSwapMode && showSelectedTerms && (
              <AnimatePresence>
                {props.selectedTerms.length > 0 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="shrink-0 overflow-hidden border-t border-border-light bg-background-secondary-medium/50 dark:border-gray-700 dark:bg-gray-800"
                  >
                    <div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto px-4 py-3">
                      {props.selectedTerms.map((term) => (
                        <motion.span
                          key={term.id}
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.8, opacity: 0 }}
                          transition={{ duration: 0.1 }}
                          className="inline-flex items-center gap-1.5 rounded-full bg-brand-100 py-1 pl-3 pr-1.5 text-sm font-medium text-brand-700 dark:bg-gray-800 dark:text-brand-400"
                        >
                          <span>{truncateText(term.name)}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              props.onRemove(term.id)
                            }}
                            className="bg-brand-200/50 flex size-5 items-center justify-center rounded-full text-brand-600 transition-colors hover:bg-brand-300 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-500"
                            aria-label={`Remove ${term.name}`}
                          >
                            <Close className="size-3" />
                          </button>
                        </motion.span>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}

            <div className="flex shrink-0 items-center justify-between border-t border-border-light bg-background-secondary-medium px-4 py-2 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center gap-4 text-xs text-text-subtle dark:text-gray-500">
                <span className="flex items-center gap-1">
                  <kbd className="rounded bg-background-quaternary px-1.5 py-0.5 font-mono text-text-body dark:bg-gray-800 dark:text-gray-400">↑</kbd>
                  <kbd className="rounded bg-background-quaternary px-1.5 py-0.5 font-mono text-text-body dark:bg-gray-800 dark:text-gray-400">↓</kbd>
                  <span>navigate</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="rounded bg-background-quaternary px-1.5 py-0.5 font-mono text-text-body dark:bg-gray-800 dark:text-gray-400">↵</kbd>
                  <span>select</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="rounded bg-background-quaternary px-1.5 py-0.5 font-mono text-text-body dark:bg-gray-800 dark:text-gray-400">esc</kbd>
                  <span>close</span>
                </span>
              </div>
              <Button
                variant="primary"
                size="md"
                type="button"
                onClick={onClose}
                className="focus-visible:ring-brand-600"
              >
                {isSwapMode ? 'Cancel' : 'Done'}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}

export default HPOSearchModal
